import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { BlockUI } from "primereact/blockui";

interface NetworkStatus {
  isOnline: boolean;
  isSlow: boolean; // Crítico (Bloqueo total)
  isWarning: boolean; // Intermedio (Bloqueo con botón de continuar)
}

export default function NetworkStatusAlert() {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isSlow: false,
    isWarning: false,
  });

  const [userBypassedWarning, setUserBypassedWarning] = useState(false);
  const wasOffline = useRef(!navigator.onLine);
  const toast = useRef<Toast>(null);
  const isChecking = useRef(false);

  useEffect(() => {
    const handleOnline = () => {
      setStatus((prev) => ({ ...prev, isOnline: true }));
      setUserBypassedWarning(false);

      if (wasOffline.current) {
        wasOffline.current = false;
        window.location.reload();
        return;
      }

      checkSpeed();
    };

    const handleOffline = () => {
      wasOffline.current = true;
      setStatus({ isOnline: false, isSlow: false, isWarning: false });
      setUserBypassedWarning(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const checkSpeed = async () => {
      if (isChecking.current || !navigator.onLine) return;
      isChecking.current = true;

      let isSlowConnection = false;
      let isWarningConnection = false;

      // 1. Connection RTT Assessment (Use as guidance, but don't hard block alone)
      const nav = navigator as any;
      const connection =
        nav.connection || nav.mozConnection || nav.webkitConnection;

      if (connection && connection.rtt > 1500) {
        // Only mark warning if API reports severe slowdown (>1500ms)
        isWarningConnection = true;
      }

      // 2. Active Ping Test to a Reliable Endpoint
      // TIP: Replace this with an lightweight endpoint on your own server (e.g., /api/ping)
      const testEndpoint = "https://1.1.1.1/cdn-cgi/trace";

      try {
        const startTime = performance.now();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3500);

        await fetch(`${testEndpoint}?cache=${Date.now()}`, {
          method: "GET",
          mode: "cors",
          cache: "no-store",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const duration = performance.now() - startTime;

        if (duration > 2000) {
          isSlowConnection = true;
        } else if (duration > 1000) {
          isWarningConnection = true;
        } else {
          // Explicitly clear warnings if ping was fast
          isSlowConnection = false;
          isWarningConnection = false;
        }
      } catch (error) {
        // DO NOT automatically assume hard failure on a single failed fetch over Wi-Fi.
        // If navigator still says online, degrade gracefully to a warning rather than a critical lock.
        if (navigator.onLine) {
          isWarningConnection = true;
        } else {
          isSlowConnection = true;
        }
      }

      setStatus((prev) => {
        return {
          isOnline: true,
          isSlow: isSlowConnection,
          isWarning: isWarningConnection,
        };
      });

      isChecking.current = false;
    };

    const intervalId = setInterval(checkSpeed, 8000); // Checked every 8s to prevent excessive polling
    checkSpeed();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(intervalId);
    };
  }, []); // Removed userBypassedWarning to prevent effect re-triggering loop

  const shouldBlockUI =
    !status.isOnline ||
    status.isSlow ||
    (status.isWarning && !userBypassedWarning);

  const isCriticalError = !status.isOnline || status.isSlow;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <BlockUI blocked={shouldBlockUI} fullScreen style={{ zIndex: 100000 }}>
        {shouldBlockUI && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalCard}>
              <div style={styles.iconContainer}>
                <i
                  className={
                    isCriticalError
                      ? "pi pi-times-circle"
                      : "pi pi-exclamation-triangle"
                  }
                  style={{
                    fontSize: "3.5rem",
                    color: isCriticalError ? "#ef4444" : "#f59e0b",
                  }}
                ></i>
              </div>

              <h3 style={styles.modalTitle}>
                {!status.isOnline && "Sin Conexión Detectada"}
                {status.isOnline &&
                  status.isSlow &&
                  "Conexión Inestable / Crítica"}
                {status.isOnline && status.isWarning && "Conexión Lenta"}
              </h3>

              <p style={styles.modalDescription}>
                {!status.isOnline &&
                  "Revisa tu red WiFi o cables de red para continuar."}
                {status.isOnline &&
                  status.isSlow &&
                  "Tu latencia es demasiado alta para operar de forma segura. Esperando estabilidad..."}
                {status.isOnline &&
                  status.isWarning &&
                  "Hemos detectado una leve degradación en tu red. ¿Deseas continuar de todas formas?"}
              </p>

              {status.isOnline && status.isWarning && (
                <div style={styles.buttonContainer}>
                  <button
                    onClick={() => setUserBypassedWarning(true)}
                    style={styles.continueButton}
                  >
                    Continuar con advertencia
                  </button>
                </div>
              )}

              <div style={styles.statusBadgeContainer}>
                <div
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: isCriticalError
                      ? "rgba(239, 68, 68, 0.15)"
                      : "rgba(245, 158, 11, 0.15)",
                    color: isCriticalError ? "#ef4444" : "#d97706",
                    borderColor: isCriticalError
                      ? "rgba(239, 68, 68, 0.3)"
                      : "rgba(245, 158, 11, 0.3)",
                  }}
                >
                  <span style={styles.badgeDot}>●</span>
                  {isCriticalError
                    ? "UI Bloqueada Totalmente"
                    : "Operación bajo aviso"}
                </div>
              </div>
            </div>
          </div>
        )}
      </BlockUI>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 100005,
    backgroundColor: "rgba(15, 23, 42, 0.75)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },
  modalCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "35px 30px",
    maxWidth: "420px",
    width: "90%",
    textAlign: "center",
    color: "#0f172a",
    fontFamily: "system-ui, sans-serif",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
  },
  iconContainer: { margin: "0 auto 16px auto" },
  modalTitle: { margin: "0 0 10px 0", fontSize: "20px", fontWeight: "700" },
  modalDescription: {
    margin: "0 0 24px 0",
    fontSize: "14px",
    color: "#475569",
    lineHeight: "1.5",
  },
  buttonContainer: {
    marginBottom: "20px",
  },
  continueButton: {
    backgroundColor: "#0f172a",
    color: "#ffffff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    width: "100%",
  },
  statusBadgeContainer: { display: "flex", justifyContent: "center" },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    border: "1px solid",
  },
  badgeDot: { fontSize: "8px" },
};
